import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';

/**
 * Renders a full-viewport modal overlay into `document.body`.
 *
 * Why every modal needs this. A `position: fixed` element is positioned
 * against the viewport ONLY while no ancestor creates a containing block for
 * it. `transform`, `filter`, `perspective`, `will-change`, `contain: paint`
 * and `backdrop-filter` all create one, and `position: sticky`, `opacity < 1`
 * and any `z-index` on a positioned ancestor create a stacking context. Inside
 * either, a `fixed inset-0 z-50` overlay stops covering the viewport and stops
 * outranking siblings: it paints beneath the page, so the modal is invisible
 * and unclickable while React still believes it is open. Nothing throws and
 * nothing reaches analytics.
 *
 * That is not hypothetical. `ConfirmDialog` rendered inline inside the sticky
 * <aside> sidebar (DevResetButton), the reset dialog painted behind the
 * dashboard, and the account reset silently never fired. ConfirmDialog was
 * portaled in response; this component is that fix generalised so the next
 * modal cannot rediscover it.
 *
 * Portaling also makes stacking ORDER predictable. Two inline overlays at the
 * same z-index resolve by DOM order, so which one wins depends on where each
 * happens to sit in the render tree. Portaled, order is mount order: a modal
 * opened on top of another is appended later and therefore paints on top,
 * which is what a reader expects and what a refactor cannot silently break.
 *
 * React portals keep context and event bubbling intact, so a portaled modal
 * behaves identically to an inline one in every respect except paint order.
 *
 * Guards: `__tests__/modalPortalParity.source.test.ts` (the class) and
 * `__tests__/modalPortal.behaviour.test.tsx` (the escape, measured).
 */
export function ModalPortal({ children }: { children: ReactNode }) {
  return createPortal(children, document.body);
}
